import type { ApolloCache, Reference } from '@apollo/client';
import gql from 'graphql-tag';
import { partition, uniq } from 'lodash';
import type { Apollo } from 'src/gql/generated';
import { Sentry } from 'src/sentry';

const ARTIFACT_REPORT_FRAGMENT = gql`
  fragment ArtifactReportFields on Report {
    id
    name
  }
`;

/**
 * Inputs to {@link applyReportArtifactCreatedEventToCache}. Mirrors
 * `ArtifactCreatedEventForCache` for documents — exported separately so
 * tests can construct it without the full subscription payload chain.
 */
export interface ReportArtifactCreatedEventForCache {
  agentMessageId: string;
  artifactCreatedAt: string;
  kindList: Apollo.AgentMessageJoinKind[];
  reportId: string;
  reportName: string;
  threadId: string;
}

type ThreadArtifactCacheRow = NonNullable<
  Apollo.GetAgentThreadQuery['agentThread']
>['artifacts'][number];
type ThreadReportArtifactCacheRow = Extract<
  ThreadArtifactCacheRow,
  { __typename: 'ThreadReportArtifact' }
>;

/**
 * Apply a `ReportArtifactCreatedEvent` to the Apollo cache: seed the
 * Report entity (so the bar renders the name immediately) and prepend a
 * fresh ThreadReportArtifact-shaped row onto the parent AgentThread.artifacts
 * list. Pure with respect to its inputs — the
 * cache is the only side effect, mirroring `applyArtifactCreatedEventToCache`.
 */
export function applyReportArtifactCreatedEventToCache(
  cache: ApolloCache,
  event: ReportArtifactCreatedEventForCache
): void {
  const row: ThreadReportArtifactCacheRow = {
    __typename: 'ThreadReportArtifact' as const,
    agentMessageId: event.agentMessageId,
    createdAt: event.artifactCreatedAt,
    kindList: event.kindList,
    report: {
      __typename: 'Report' as const,
      id: event.reportId,
      name: event.reportName,
    },
  };
  cache.writeFragment({
    data: row.report,
    fragment: ARTIFACT_REPORT_FRAGMENT,
    id: cache.identify({ __typename: 'Report', id: row.report.id }),
  });

  const threadCacheId = cache.identify({ __typename: 'AgentThread', id: event.threadId });
  /**
   * If the parent AgentThread isn't in cache yet (subscription event raced
   * the initial query), `cache.modify` silently no-ops and we'd lose the
   * row. Probe with `readFragment` first; on miss, log so the rate is
   * observable, and rely on the next GetAgentThread to materialize the row.
   */
  const threadHasArtifactsField =
    threadCacheId !== undefined &&
    cache.readFragment({
      fragment: gql`
        fragment AgentThreadIdProbe on AgentThread {
          id
        }
      `,
      id: threadCacheId,
    }) !== null;
  if (!threadHasArtifactsField) {
    Sentry.captureMessage('ReportArtifactCreatedEvent arrived before AgentThread cache entry', {
      extra: { reportId: event.reportId, threadId: event.threadId },
      level: 'warning',
    });
    return;
  }

  cache.modify({
    fields: {
      artifacts(existing = [], { readField }) {
        if (!Array.isArray(existing)) return existing;

        const [matchingRows, remainingRows] = partition(existing, artifact => {
          const reportRef = readField<Reference>(
            'report' satisfies keyof ThreadReportArtifactCacheRow,
            artifact
          );
          return readField('id', reportRef) === event.reportId;
        });
        const existingKindList = matchingRows.flatMap(
          existingRow =>
            readField<readonly Apollo.AgentMessageJoinKind[]>(
              'kindList' satisfies keyof ThreadReportArtifactCacheRow,
              existingRow
            ) ?? []
        );

        return [
          {
            ...row,
            kindList: uniq([...existingKindList, ...event.kindList]),
          },
          ...remainingRows,
        ];
      },
    },
    id: threadCacheId,
  });
}
